/**
 * Terms of Service Page
 *
 * Professional placeholder template - customize with your actual terms
 */

import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-4"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Terms of Service
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg dark:prose-invert max-w-none">

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Welcome to myTotTot ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our baby tracking application and services (collectively, the "Service").
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.
            </p>
          </section>

          {/* Account Registration */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              2. Account Registration
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              To use our Service, you must:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Be at least 18 years old or have parental consent</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          {/* Privacy and Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              3. Privacy and Data Security
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              myTotTot is built with privacy as a core principle:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Zero-Knowledge Encryption:</strong> All baby data, events, and notes are encrypted on your device before being sent to our servers</li>
              <li><strong>No Access:</strong> We cannot read your encrypted data, even with server access</li>
              <li><strong>Your Keys:</strong> You control the encryption keys through your account password</li>
              <li>For more details, please read our <Link to="/privacy" className="text-primary-600 hover:text-primary-700">Privacy Policy</Link></li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              4. Acceptable Use
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to breach security or authentication measures</li>
              <li>Interfere with the proper functioning of the Service</li>
              <li>Upload malicious code or content</li>
              <li>Impersonate others or misrepresent your affiliation</li>
              <li>Harvest or collect data from other users</li>
            </ul>
          </section>

          {/* Medical Disclaimer */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              5. Medical Disclaimer
            </h2>
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4 mb-4">
              <p className="text-amber-800 dark:text-amber-200 font-semibold">
                Important: myTotTot is NOT a medical service
              </p>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Our Service is designed for tracking and information purposes only. It does not:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Provide medical advice, diagnosis, or treatment</li>
              <li>Replace professional medical consultation</li>
              <li>Make health recommendations</li>
              <li>Emergency services - always call emergency services for urgent medical needs</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              Always consult with qualified healthcare providers regarding your baby's health.
            </p>
          </section>

          {/* Data Backup */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              6. Data Backup and Loss
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              While we implement robust backup systems:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>You are responsible for maintaining your own backups of important data</li>
              <li>We are not liable for any data loss, corruption, or destruction</li>
              <li>Our data export feature allows you to download your data regularly</li>
              <li>Lost encryption keys cannot be recovered - keep your password safe</li>
            </ul>
          </section>

          {/* Sharing and Invitations */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              7. Sharing and Invitations
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              When sharing baby profiles:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>You are responsible for who you grant access to</li>
              <li>Shared users gain access to all data for that baby profile</li>
              <li>You can revoke access at any time</li>
              <li>Emergency access features should only be used with trusted individuals</li>
            </ul>
          </section>

          {/* Service Availability */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              8. Service Availability
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We strive to provide reliable service, but we do not guarantee:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mt-4">
              <li>Uninterrupted or error-free operation</li>
              <li>Availability at all times (maintenance may be required)</li>
              <li>Compatibility with all devices or browsers</li>
              <li>That the Service will meet your specific requirements</li>
            </ul>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              9. Termination
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We reserve the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Suspend or terminate your account for violations of these Terms</li>
              <li>Modify or discontinue the Service at any time</li>
              <li>Remove content that violates these Terms</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              You may terminate your account at any time through the settings page. Upon termination, you may export your data before account deletion.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              10. Limitation of Liability
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>The Service is provided "AS IS" without warranties of any kind</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount you paid for the Service in the past 12 months</li>
              <li>We are not responsible for third-party services or links</li>
            </ul>
          </section>

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              11. Changes to Terms
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We may update these Terms from time to time. We will notify you of significant changes by email or through the Service. Your continued use after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              12. Governing Law
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              These Terms are governed by the laws of [Your Jurisdiction]. Any disputes shall be resolved in the courts of [Your Jurisdiction].
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              13. Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Email:</strong> <a href="mailto:support@mytottot.app" className="text-primary-600 hover:text-primary-700">support@mytottot.app</a>
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} myTotTot. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Terms of Service
              </Link>
              <a href="mailto:support@mytottot.app" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
